import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
} from "@mui/material";

import { IoIosCloseCircleOutline } from "react-icons/io";


const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

export default function MultiMonthSelect({ value = [], onChange }) {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(value);
    const [year, setYear] = useState(dayjs().year());
    const currentMonth = dayjs().month();
    const currentYear = dayjs().year();



    useEffect(() => {
        if (!value || value.length === 0) {
            setSelected([{ month: currentMonth, year: currentYear }]);
            onChange?.([{ month: currentMonth, year: currentYear }]);
        } else {
            setSelected(value);
        }
    }, [value, currentMonth, currentYear]);

    const toggleMonth = (month) => {
    const monthIndex = (y, m) => y * 12 + m;
    const clickedIndex = monthIndex(year, month);
    const currentIndex = monthIndex(currentYear, currentMonth);

    // Prevent selecting future months
    // if (clickedIndex > currentIndex) return;

    // If nothing selected yet
    if (selected.length === 0) {
        const newSelected = [{ month, year }];
        setSelected(newSelected);
        onChange?.(newSelected);
        return;
    }

    // Sort selection
    const sorted = [...selected].sort((a, b) =>
        a.year === b.year ? a.month - b.month : a.year - b.year
    );
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    const firstIndex = monthIndex(first.year, first.month);
    const lastIndex = monthIndex(last.year, last.month);

    let newSelected = [];

    // Clicked before the first month (extend backward)
    if (clickedIndex < firstIndex) {
        for (let i = clickedIndex; i <= lastIndex; i++) {
            const y = Math.floor(i / 12);
            const m = i % 12;
            newSelected.push({ month: m, year: y });
        }
    }
    // Clicked after the last month (extend forward)
    else if (clickedIndex > lastIndex) {
        for (let i = firstIndex; i <= clickedIndex; i++) {
            const y = Math.floor(i / 12);
            const m = i % 12;
            newSelected.push({ month: m, year: y });
        }
    }
    // Clicked exactly the first month (deselect it)
    else if (clickedIndex === firstIndex && selected.length > 1) {
        for (let i = firstIndex + 1; i <= lastIndex; i++) {
            const y = Math.floor(i / 12);
            const m = i % 12;
            newSelected.push({ month: m, year: y });
        }
    }
    // Clicked exactly the last month (deselect it)
    else if (clickedIndex === lastIndex && selected.length > 1) {
        for (let i = firstIndex; i <= lastIndex - 1; i++) {
            const y = Math.floor(i / 12);
            const m = i % 12;
            newSelected.push({ month: m, year: y });
        }
    }
    // Clicked inside the range → trim to closest edge
    else if (clickedIndex > firstIndex && clickedIndex < lastIndex) {
        const distToFirst = clickedIndex - firstIndex;
        const distToLast = lastIndex - clickedIndex;

        if (distToFirst <= distToLast) {
            // Trim start
            for (let i = clickedIndex; i <= lastIndex; i++) {
                const y = Math.floor(i / 12);
                const m = i % 12;
                newSelected.push({ month: m, year: y });
            }
        } else {
            // Trim end
            for (let i = firstIndex; i <= clickedIndex; i++) {
                const y = Math.floor(i / 12);
                const m = i % 12;
                newSelected.push({ month: m, year: y });
            }
        }
    } else {
        // Only one month left, clicking it should reselect it alone
        newSelected = [{ month, year }];
    }

    setSelected(newSelected);
    onChange?.(newSelected);
};



    const handleClear = () => {
        const currentMonthObj = { month: currentMonth, year: currentYear };
        setSelected([currentMonthObj]);
        onChange?.([currentMonthObj]);
    };


    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Button
                    variant="outlined"
                    onClick={() => setOpen(true)}
                    sx={{
                        borderColor: "#D3D3D3",
                        color: "#333",
                        backgroundColor: "#fff",
                        textTransform: "none",
                        // borderRadius: "20px",

                        height: "40px",
                        "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#bbb" },
                        fontWeight: 500,
                        // marginTop: '8px',
                        width: '280px'
                    }}

                >
                    Select Billing Period
                </Button>
                {selected.length > 0 && (
                    <>
                        {selected.map((m, idx) => {
                            const isCurrentMonthOnly = selected.length === 1 && m.month === currentMonth && m.year === currentYear;
                            const isFirst = idx === 0;
                            const isLast = idx === selected.length - 1;
                            const canDelete = (isFirst || isLast) && selected.length > 1;

                            return (
                                <Chip
                                    key={`${m.month}-${m.year}`}
                                    label={`${months[m.month]}’${String(m.year).slice(-2)}`}
                                    onDelete={
                                        canDelete
                                            ? () => {
                                                // Remove first or last month
                                                let newSelected = [...selected];
                                                if (isFirst) newSelected = newSelected.slice(1);
                                                else if (isLast) newSelected = newSelected.slice(0, -1);
                                                setSelected(newSelected);
                                                onChange?.(newSelected);
                                            }
                                            : undefined
                                    }
                                    variant="outlined"
                                    sx={{
                                        borderRadius: "20px",
                                        fontSize: "0.9rem",
                                        color: "#333",
                                        borderColor: "#D3D3D3",
                                        backgroundColor: "#f9f9f9",
                                        "& .MuiChip-deleteIcon": {
                                            color: "#999",
                                            fontSize: "18px",
                                            "&:hover": { color: "#333" },
                                        },
                                    }}
                                />
                            );
                        })}
                        {selected.length > (selected.some((m) => m.month === currentMonth && m.year === currentYear) ? 1 : 0) && (
                            <Button
                                size="small"
                                onClick={handleClear}
                                sx={{
                                    fontWeight: 500,
                                    textTransform: "none",
                                    color: "#555",
                                    "&:hover": { background: "transparent", color: "#000" },
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                    </>
                )}
            </Box>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: { borderRadius: "12px", backgroundColor: "#fafafa" },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: 600,
                        color: "#333",
                    }}
                >
                    Select Months ({year})
                    <IconButton onClick={() => setOpen(false)}>
                        {/* <CloseIcon sx={{ color: "#555" }} /> */}
                        <IoIosCloseCircleOutline style={{ color: "#555" }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={1}>
                        {months.map((m, idx) => {
                            const isSelected = selected.some(
                                (sel) => sel.month === idx && sel.year === year
                            );
                            return (
                                <Grid item xs={3} key={idx}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => toggleMonth(idx)}
                                        // disabled={isDisabled}
                                        sx={{
                                            textTransform: "none",
                                            borderColor: isSelected ? "#666" : "#ccc",
                                            backgroundColor: isSelected ? "#e0e0e0" : "#fff",
                                            color: "#333",
                                            "&:hover": {
                                                backgroundColor: isSelected ? "#d5d5d5" : "#f3f3f3",
                                            },
                                        }}
                                    >
                                        {m}
                                    </Button>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Box mt={2} display="flex" justifyContent="space-between">
                        <Button
                            onClick={() => setYear(year - 1)}
                            sx={{
                                textTransform: "none",
                                color: "#555",
                                "&:hover": { background: "#f0f0f0" },
                            }}
                        >
                            ◀ Prev
                        </Button>
                        <Button
                            onClick={() => setYear(year + 1)}
                            sx={{
                                textTransform: "none",
                                color: "#555",
                                "&:hover": { background: "#f0f0f0" },
                            }}
                        >
                            Next ▶
                        </Button>
                    </Box>

                    <Box mt={2} textAlign="right">
                        <Button
                            variant="contained"
                            onClick={() => setOpen(false)}
                            sx={{
                                textTransform: "none",
                                backgroundColor: "#666",
                                "&:hover": { backgroundColor: "#555" },
                            }}
                        >
                            Done
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
